import { useEffect, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { CloseIcon } from "../lib/icons";

/**
 * Corner confirmation — "Product saved". Trailing-bottom, toastIn 160ms.
 *
 * `role="status"` with `aria-live="polite"`: the message is announced when it
 * appears without interrupting whatever the user is doing. An `alert` role
 * here would be wrong — a save confirmation is not an emergency, and making
 * every success barge in is how people learn to ignore announcements. A
 * `danger` toast is the exception and does use `alert`.
 *
 * Four things here go BEYOND the design's specimen, which drew one toast once:
 *
 * 1. A 24px close target. The design's is 12px, half the WCAG 2.5.8 minimum —
 *    and the padding is on the button, not the glyph, so the mark stays the
 *    size it was drawn.
 * 2. A minimum width, so a stack of messages is not ragged. Without it the
 *    four tones measure 122 to 158px and read as a mistake.
 * 3. An `action` slot. "Undo" is the most valuable thing a save confirmation
 *    can offer and the specimen had nowhere to put it.
 * 4. A leading icon per tone. Resting the whole distinction between saved and
 *    failed on a 7px dot means, at a glance, they look the same.
 *
 * Positioning belongs to `ToastViewport`, not here: a toast that fixes itself
 * to the corner cannot be stacked, and stacking is the point.
 */
export type ToastProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  tone?: "success" | "danger" | "info" | "warning";
  /** Milliseconds before auto-dismiss. Omit to require a manual close. */
  duration?: number;
  closeLabel: string;
  /** One control — "Undo", "View order". Never more. */
  action?: ReactNode;
  className?: string;
};

const TONE = {
  success: "text-success",
  danger: "text-danger",
  info: "text-info",
  warning: "text-warning",
} as const;

/**
 * A distinct SHAPE per tone, not four colours of the same dot. Colour alone
 * fails for the one in twelve men who cannot separate the red from the green.
 */
const MARK = {
  success: "M3 8.5 6.5 12 13 4.5",
  danger: "M8 3.5v5.5M8 12h.01",
  warning: "M8 3.5v5.5M8 12h.01",
  info: "M8 12.5V7M8 4h.01",
} as const;

export function Toast({
  open,
  onClose,
  children,
  tone = "success",
  duration,
  closeLabel,
  action,
  className,
}: ToastProps) {
  useEffect(() => {
    if (!open || !duration) return;
    const t = setTimeout(onClose, duration);
    return () => clearTimeout(t);
  }, [open, duration, onClose]);

  if (!open) return null;

  return (
    <div
      // A failure interrupts; a success waits its turn.
      role={tone === "danger" ? "alert" : "status"}
      aria-live={tone === "danger" ? "assertive" : "polite"}
      className={cn(
        "pointer-events-auto flex min-w-[280px] max-w-[min(420px,calc(100vw-32px))]",
        "items-center gap-2.5 rounded-panel border border-border2 bg-field",
        "py-2 pe-2 ps-3.5 text-secondary text-text shadow-menu animate-toast-in",
        className,
      )}
    >
      <svg
        viewBox="0 0 16 16"
        aria-hidden="true"
        className={cn("size-3.5 flex-none", TONE[tone])}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {tone === "success" ? null : <circle cx="8" cy="8" r="6.25" />}
        <path d={MARK[tone]} />
      </svg>

      <span className="flex-1"><bdi>{children}</bdi></span>

      {action ? <span className="flex-none">{action}</span> : null}

      <button
        type="button"
        onClick={onClose}
        aria-label={closeLabel}
        title={closeLabel}
        className={cn(
          // 24px, not the design's 12px: WCAG 2.5.8 puts the floor at 24, and
          // the padding goes on the button so the glyph stays as drawn.
          "grid size-6 flex-none place-items-center rounded-[4px]",
          "cursor-pointer text-faint transition-colors",
          "hover:bg-raised2 hover:text-text",
          "outline-none focus-visible:ring-2 focus-visible:ring-info/60",
        )}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

/**
 * Where toasts live: the trailing-bottom corner, newest nearest the edge.
 *
 * The old `Toast` fixed itself to the corner, so a second save replaced the
 * first and the seller never saw it. This owns the position and the stack; the
 * toasts inside it are ordinary flow content.
 *
 * `pointer-events-none` on the viewport with `auto` on each toast: the column
 * spans a corner of the screen, and without this it swallows clicks on
 * whatever is behind it even when it is empty.
 */
export type ToastViewportProps = {
  children: ReactNode;
  className?: string;
};

export function ToastViewport({ children, className }: ToastViewportProps) {
  return (
    <div
      className={cn(
        "pointer-events-none fixed bottom-5 end-5 z-40",
        "flex flex-col-reverse items-end gap-2",
        className,
      )}
    >
      {children}
    </div>
  );
}
